// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

type StoresSortBy = components["schemas"]["StoresSortByEnum"];
type StoresSettingsSortBy = components["schemas"]["StoresSettingsSortByEnum"];
type SortOrder = components["schemas"]["HttpSortingOrderEnum"];

// --- List ---

interface StoresListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: StoresSortBy;
  orderBy?: SortOrder;
  chainName?: string;
  regionName?: string;
  onlyActive?: boolean;
  enabled?: boolean;
}

export function useStoresQuery(params: StoresListParams = {}) {
  const { page = 1, limit = 20, search, sortBy, orderBy, chainName, regionName, onlyActive, enabled } = params;

  return useQuery({
    queryKey: ["stores", "list", { page, limit, search, sortBy, orderBy, chainName, regionName, onlyActive }],
    enabled,
    queryFn: async () => {
      const res = await api.GET("/v1/stores", {
        params: {
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[chainName]": chainName || undefined,
            "filter[regionName]": regionName || undefined,
            "filter[onlyActive]": onlyActive,
          },
        },
      });
      return res.data as unknown as {
        stores: Array<{
          storeId: number;
          storeName: string;
          legibleName: string;
          storeCode?: string;
          chainId: number;
          timeZone: string;
          regionName?: string;
          isActive: boolean;
        }>;
        chains: Array<{
          chainId: number;
          chainName: string;
          legibleName: string;
          defaultTimeZone: string;
        }>;
        count: number;
      };
    },
  });
}

// --- Detail ---

export function useStoreDetailQuery(storeName: string) {
  return useQuery({
    queryKey: ["stores", "detail", storeName],
    queryFn: async () => {
      const res = await api.GET("/v1/stores/{store_name}", {
        params: { path: { store_name: storeName } },
      });
      if (res.error) throw new Error("Failed to load store");
      return res.data;
    },
    enabled: !!storeName,
  });
}

// --- Create ---

export function useCreateStoreMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      copyFrom,
      body,
    }: {
      copyFrom?: string;
      body: components["schemas"]["StoresCreateRequest"];
    }) => {
      const res = await api.POST("/v1/stores", {
        params: { query: { copyFrom: copyFrom || undefined } },
        body,
      });
      if (res.error) throw new Error("Failed to create store");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores"] });
      queryClient.invalidateQueries({ queryKey: ["chains"] });
      // Refresh the shared backoffice context so the new store appears in pickers/filters everywhere.
      queryClient.invalidateQueries({ queryKey: ["backoffice", "context"] });
    },
  });
}

// --- Edit ---

export function useEditStoreMutation(storeName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["StoresEditRequest"]) => {
      const res = await api.PATCH("/v1/stores/{store_name}", {
        params: { path: { store_name: storeName } },
        body,
      });
      if (res.error) throw new Error("Failed to update store");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores"] });
      queryClient.invalidateQueries({ queryKey: ["chains"] });
    },
  });
}

// --- Activate / Deactivate ---

export function useToggleStoreActiveMutation(storeName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (isActive: boolean) => {
      const res = await api.PATCH("/v1/stores/{store_name}/active", {
        params: { path: { store_name: storeName } },
        body: { isActive },
      });
      if (res.error) throw new Error("Failed to update");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores"] });
      queryClient.invalidateQueries({ queryKey: ["chains"] });
    },
  });
}

// --- Settings ---

export interface StoreSettingEntry {
  settingId: number;
  key: string;
  name: string;
  description: string;
  groupName: string;
  fieldType: "boolean" | "number" | "string" | "list" | "json" | "enum";
  enumValues?: string[];
  defaultValue: string;
  overrideId?: number;
  overrideValue?: string;
  overrideSource: "default" | "chain" | "store";
}

interface StoreSettingsListParams {
  storeName: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: StoresSettingsSortBy;
  orderBy?: SortOrder;
  group?: string;
  onlyOverrides?: boolean;
}

export function useStoreSettingsQuery(params: StoreSettingsListParams) {
  const { storeName, page = 1, limit = 20, search, sortBy, orderBy, group, onlyOverrides } = params;

  return useQuery({
    queryKey: ["stores", "settings", storeName, { page, limit, search, sortBy, orderBy, group, onlyOverrides }],
    queryFn: async () => {
      const res = await api.GET("/v1/stores/{store_name}/settings", {
        params: {
          path: { store_name: storeName },
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[group]": group || undefined,
            "filter[onlyOverrides]": onlyOverrides,
          },
        },
      });
      return res.data as unknown as {
        settings: StoreSettingEntry[];
        count: number;
      };
    },
    enabled: !!storeName,
  });
}

export function useAddStoreSettingMutation(storeName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { settingId: number; newValue: string }) => {
      const res = await api.POST("/v1/stores/{store_name}/settings", {
        params: { path: { store_name: storeName } },
        body,
      });
      if (res.error) throw new Error("Failed to add");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["stores", "settings", storeName],
      });
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

export function useEditStoreSettingMutation(storeName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { overrideId: number; newValue: string }) => {
      const res = await api.PATCH("/v1/stores/{store_name}/settings", {
        params: { path: { store_name: storeName } },
        body: [body],
      });
      if (res.error) throw new Error("Failed to update");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["stores", "settings", storeName],
      });
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

export function useDeleteStoreSettingMutation(storeName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (overrideId: number) => {
      const res = await api.DELETE("/v1/stores/{store_name}/settings", {
        params: { path: { store_name: storeName } },
        body: [{ overrideId }],
      });
      if (res.error) throw new Error("Failed to delete");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["stores", "settings", storeName],
      });
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

// --- Map Upload ---

export function useUploadStoreMapMutation(storeName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("map_file", file);
      const token = localStorage.getItem("access_token");
      const baseUrl = import.meta.env.VITE_API_URL;
      const res = await fetch(`${baseUrl}/v1/stores/${encodeURIComponent(storeName)}/map`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!res.ok) throw new Error("Failed to upload map");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["stores", "detail", storeName],
      });
    },
  });
}
